/* ----------------------------------------------------------------------------
 * Typed API client for api.bookedcentral.com/v1 (design-brief-v1 §8).
 *
 * - Cookie-session auth: every request sends `credentials: 'include'` so the
 *   session cookie (being added by the auth agent) rides along.
 * - CSRF: a double-submit token read from the `bc_csrf` cookie is echoed in the
 *   `X-CSRF-Token` header on mutating requests.
 * - Envelope: success responses return the JSON body directly; errors use the
 *   house `{ code, message, data?: { status } }` shape (ControllerSupport).
 * - Pagination: cursor + limit query params, matching the API.
 * -------------------------------------------------------------------------- */

const API_BASE = import.meta.env.VITE_API_BASE ?? 'https://api.bookedcentral.com/v1';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
    /** The full parsed error body (additive). Carries top-level blocks the
     *  house envelope places beside `code` — e.g. the 409 `duplicate` block —
     *  that `details` (code/data/details only) does not. Optional; existing
     *  callers are unaffected. */
    public readonly payload?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

interface RequestOptions {
  method?: Method;
  body?: unknown;
  query?: Record<string, string | number | undefined>;
  signal?: AbortSignal;
  /** Explicit idempotency key for a mutation. When set, it is sent verbatim as
   *  X-Bksesh-Idempotency-Key instead of a fresh per-call UUID, so a deliberate
   *  retry of the SAME user decision (e.g. an approval) is de-duplicated by the
   *  server replay cache rather than applied twice. Additive; unset = prior
   *  fresh-UUID behaviour. */
  idempotencyKey?: string;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const base = API_BASE.replace(/\/$/, '');
  const rel = path.startsWith('/') ? path : `/${path}`;
  // `base` may be absolute ("https://api…/v1") or a proxy path ("/v1").
  const qs = new URLSearchParams();
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== '') qs.set(k, String(v));
    }
  }
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return `${base}${rel}${suffix}`;
}

export async function apiRequest<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, signal, idempotencyKey } = opts;
  const headers: Record<string, string> = { Accept: 'application/json' };

  if (body !== undefined) headers['Content-Type'] = 'application/json';

  // CSRF double-submit + idempotency on mutations.
  if (method !== 'GET') {
    const csrf = readCookie('bc_csrf');
    if (csrf) headers['X-CSRF-Token'] = csrf;
    // A caller-supplied key rides verbatim so a deliberate retry of the SAME
    // user action (e.g. an approval decision) de-dupes at the server replay
    // cache; otherwise a fresh UUID per action (each is a distinct write).
    headers['X-Bksesh-Idempotency-Key'] = idempotencyKey ?? crypto.randomUUID();
  }

  let res: Response;
  try {
    res = await fetch(buildUrl(path, query), {
      method,
      headers,
      credentials: 'include', // cookie-session
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal,
    });
  } catch (networkErr) {
    throw new ApiError(0, 'network_error', (networkErr as Error).message || 'Network request failed');
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const payload = text ? safeJson(text) : undefined;

  if (!res.ok) {
    // Two envelopes exist in the wild: the standalone data API's nested
    // `{ error: { code, message, details } }` (platform Router / api-contracts)
    // and the flat `{ code, message, data }` shape. Support both.
    const nested =
      payload && typeof payload.error === 'object' && payload.error !== null
        ? (payload.error as Record<string, unknown>)
        : undefined;
    const code = (nested?.code as string) ?? (payload?.code as string) ?? 'error';
    const message = (nested?.message as string) ?? (payload?.message as string) ?? res.statusText;

    // Session expired/invalid on a DATA call → back to login. /auth/* handles
    // its own 401s (login failure, auth/me probe) — never redirect those.
    if (res.status === 401 && !path.replace(/^\//, '').startsWith('auth')) {
      redirectToLogin();
    }

    throw new ApiError(
      res.status,
      code,
      message,
      nested?.details ?? payload?.data ?? payload?.details,
      payload,
    );
  }

  return payload as T;
}

/**
 * Binary upload helper (direct upload, multipart-free). Sends raw image bytes
 * with an explicit content-type, carrying the SAME cookie-session + CSRF +
 * idempotency treatment as a JSON mutation. Used by the Brand-Kit logo upload
 * so the browser never has to base64-inflate the payload.
 */
export async function apiUpload<T>(
  path: string,
  bytes: Blob | ArrayBuffer,
  contentType: string,
  signal?: AbortSignal,
): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json', 'Content-Type': contentType };
  const csrf = readCookie('bc_csrf');
  if (csrf) headers['X-CSRF-Token'] = csrf;
  headers['X-Bksesh-Idempotency-Key'] = crypto.randomUUID();

  let res: Response;
  try {
    res = await fetch(buildUrl(path), {
      method: 'POST',
      headers,
      credentials: 'include',
      body: bytes,
      signal,
    });
  } catch (networkErr) {
    throw new ApiError(0, 'network_error', (networkErr as Error).message || 'Network request failed');
  }

  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const payload = text ? safeJson(text) : undefined;

  if (!res.ok) {
    const nested =
      payload && typeof payload.error === 'object' && payload.error !== null
        ? (payload.error as Record<string, unknown>)
        : undefined;
    const code = (nested?.code as string) ?? (payload?.code as string) ?? 'error';
    const message = (nested?.message as string) ?? (payload?.message as string) ?? res.statusText;
    if (res.status === 401 && !path.replace(/^\//, '').startsWith('auth')) redirectToLogin();
    throw new ApiError(res.status, code, message, nested?.details ?? payload?.data ?? payload?.details);
  }

  return payload as T;
}

let redirecting = false;
/** Hard-navigate to /login once (queries may fail in a burst on expiry). */
function redirectToLogin(): void {
  if (redirecting || window.location.pathname === '/login') return;
  redirecting = true;
  const from = window.location.pathname + window.location.search;
  window.location.assign('/login?expired=1&from=' + encodeURIComponent(from));
}

function safeJson(text: string): Record<string, unknown> | undefined {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

// Convenience verbs -----------------------------------------------------------
export const api = {
  get: <T>(path: string, query?: RequestOptions['query'], signal?: AbortSignal) =>
    apiRequest<T>(path, { method: 'GET', query, signal }),
  post: <T>(path: string, body?: unknown) => apiRequest<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => apiRequest<T>(path, { method: 'PATCH', body }),
  del: <T>(path: string) => apiRequest<T>(path, { method: 'DELETE' }),
};

/* ----------------------------------------------------------------------------
 * Mock flags — DELIBERATELY split so authentication and screen data gate
 * independently (Phase D real-auth cutover):
 *
 *   USE_MOCK_AUTH  Bypass real login with a canned user. Defaults to FALSE, so
 *                  the production build runs REAL cookie-session auth. ONLY an
 *                  explicit VITE_USE_MOCK_AUTH=true short-circuits login — a
 *                  stale legacy VITE_USE_MOCKS can NEVER silently downgrade auth
 *                  to mock (security invariant: auth is real unless opted out).
 *
 *   USE_MOCK_DATA  Render screens from src/data/mock.ts instead of live data
 *                  endpoints (gated per-source in src/data/sources.ts).
 *                  Code default remains TRUE for dev convenience, but the
 *                  PRODUCTION build sets VITE_USE_MOCK_DATA=false: screens
 *                  fetch real workspace data and render honest teach-style
 *                  empty states while the DB has no rows. Legacy
 *                  VITE_USE_MOCKS=false still forces real data.
 *                  Build-time failsafe: vite.config.ts REFUSES a
 *                  production build unless VITE_USE_MOCK_DATA is
 *                  explicitly 'false', so this TRUE default can never
 *                  silently ship the demo/mock app to production.
 * -------------------------------------------------------------------------- */
const legacyMocksOff = import.meta.env.VITE_USE_MOCKS === 'false';

export const USE_MOCK_AUTH = (import.meta.env.VITE_USE_MOCK_AUTH ?? 'false') === 'true';

export const USE_MOCK_DATA =
  (import.meta.env.VITE_USE_MOCK_DATA ?? (legacyMocksOff ? 'false' : 'true')) !== 'false';
